<template>
  <ig-page-base>
    <div class="box orders__content">
      <div class="product-import-and-export__title-box">
        <h2 class="title product-import-and-export__title has-text-weight-semibold">
          {{ $t('storePages.orders.title') }}
        </h2>
      </div>

      <div class="orders__filters">
        <div class="columns">
          <div class="column is-6">
            <form @submit.prevent="getOrderList()">
              <b-field grouped>
                <b-input
                  v-model="filters.query"
                  :placeholder="$t('storePages.orders.filters.query')"
                  expanded />
                <p class="control">
                  <b-button
                    native-type="submit"
                    type="is-primary"
                    :label="$t('storePages.orders.filters.btnSearch')"
                    :loading="loading"
                    :disabled="loading"
                  ></b-button>
                </p>
              </b-field>
            </form>
          </div>
          <div class="column has-text-right">
            <b-button
              type="is-primary"
              icon-left="filter"
              outlined
              :label="$t('storePages.orders.filters.btnFilters')"
              :disabled="loading"
              @click="showFilters = true"
            ></b-button>

            <ig-sidebar-filters
              v-model="showFilters"
              v-slot="{ filters }"
              @submit="applyFilters"
            >
              <b-field
                :label="$t('storePages.orders.filters.status.label')"
                label-position="inside">
                <b-select
                  v-model="filters.status"
                  :placeholder="$t('storePages.orders.filters.status.placeholder')"
                  expanded>
                  <option :value="status" v-for="status in listOfStatus" :key="status">
                    {{
                      $t(`storePages.orders.filters.status.options.${status}`)
                    }}
                  </option>
                </b-select>
              </b-field>
            </ig-sidebar-filters>
          </div>
        </div>
      </div>

      <hr />

      <b-table
        :loading="loading"
        :data="orders"
        backend-sorting
        default-sort="order.createdAt"
        default-sort-direction="desc"
        paginated
        pagination-simple
        backend-pagination
        @page-change="onPageChange"
        :total="total"
        :per-page="filters.limit"
        :current-page.sync="filters.page"
        @sort="sort"
        striped
        hoverable
        scrollable
        custom-detail-row
        detailed
        detail-key="id"
        :opened-detailed="orders.map(order => order.id)"
      >
        <template #empty>
          <ig-empty />
        </template>

        <template #bottom-left>
          <ig-per-page
            :total-displayed="orders.length"
            :total="total"
            :perPage="filters.limit"
            @change="changePerPage"
          />
        </template>

        <b-table-column
          field="order.orderNumber"
          :label="$t('storePages.orders.table.id')"
          width="150"
          sortable
        >
          <template v-slot="props">
            <span>#{{ props.row.order.orderNumber }}</span>
          </template>
        </b-table-column>

        <b-table-column
          field="order.createdAt"
          :label="$t('storePages.orders.table.createdAt')"
          sortable
        >
          <template v-slot="props">
            {{ getFormattedDate(props.row.order.createdAt) }}
          </template>
        </b-table-column>

        <b-table-column
          width="200"
          :label="$t('storePages.orders.table.customer')"
          sortable
        >
          <template v-slot="props">
            {{ props.row.customer.name || '-' }}
          </template>
        </b-table-column>

        <b-table-column
          :label="$t('storePages.orders.table.price')"
        >
          <template v-slot="props">
            {{ getFormattedPrice(props.row.order.total) }}
          </template>
        </b-table-column>

        <b-table-column
          :label="$t('storePages.orders.table.status')"
          centered
          sortable
        >
          <template>
            <span></span>
<!--            <b-tag>-->
<!--              {{ getStatusLabel(props.row.order.status) }}-->
<!--            </b-tag>-->
          </template>
        </b-table-column>

        <template slot="detail" slot-scope="props">
          <tr v-for="(fulfillment, fulfillmentKey) in props.row.fulfillments" :key="fulfillment.id">
            <td></td>
            <td>
              &nbsp;&nbsp;&nbsp;&nbsp; #{{ props.row.order.orderNumber }}-{{ fulfillmentKey + 1 }}
            </td>
            <td colspan="2">
              <small class="is-block" v-for="item in fulfillment.items" :key="item.sku">
                {{ item.qty }}x {{ item.name }} ({{ item.sku }})
              </small>
            </td>
            <td>
              {{ getFormattedPrice(fulfillment.total) }}
            </td>
            <td class="has-text-centered">
              <b-tag :type="getFulfillmentStatus(fulfillment.status)">
                {{ $t(`storePages.orders.filters.status.options.${fulfillment.status}`) }}
              </b-tag>
            </td>
          </tr>
        </template>
      </b-table>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import * as Moment from 'moment';

import {
  IgPageBase,
  IgEmpty,
  IgSidebarFilters,
  IgPerPage,
} from '@integrai/ui';

const { mapState, mapActions } = createNamespacedHelpers('orders');

export default Vue.extend({
  name: 'page-orders',
  components: {
    IgPageBase,
    IgEmpty,
    IgSidebarFilters,
    IgPerPage,
  },
  data() {
    return {
      showFilters: false,
      filters: {
        limit: 10,
        page: 1,
        sortField: 'order.createdAt',
        sortOrder: 'desc',
      },
      listOfStatus: [
        'WAITING_STOCK',
        'PROCESSING',
        'UNPACKED',
        'PACKED',
        'DISPATCHED',
        'IN_TRANSIT',
        'DELIVERED',
        'CANCELED',
      ],
    };
  },
  computed: {
    ...mapState([
      'loading',
      'orders',
      'total',
    ]),
  },
  methods: {
    ...mapActions([
      'getOrders',
    ]),
    getFormattedPrice(price) {
      if (price !== undefined) {
        return price.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          style: 'currency',
          currency: 'BRL',
        });
      }

      return '-';
    },
    getPaymentMethodLabel(method) {
      const labels = {
        BOLETO: 'Boleto',
        CREDIT_CARD: 'Cartão de Cŕedito',
        PIX: 'Pix',
      };

      return labels[method] || '-';
    },
    getStatusLabel(status) {
      const labels = {
        pending: 'Pendente',
        processing: 'Processando',
        authorized: 'Autorizado',
        paid: 'Pago',
        invoiced: 'Faturado',
        shipped: 'Enviado',
        delivered: 'Entregue',
        refunded: 'Reembolso',
        refused: 'Recusado',
        canceled: 'Cancelado',
      };

      return labels[status] || '-';
    },
    getStatusType(status) {
      const types = {
        pending: 'is-warning',
        processing: 'is-default',
        authorized: 'is-success',
        paid: 'is-success',
        invoiced: 'is-success',
        shipped: 'is-success',
        delivered: 'is-success',
        refunded: 'is-warning',
        refused: 'is-danger',
        canceled: 'is-danger',
      };

      return types[status] || 'is-default';
    },
    getFulfillmentStatus(status) {
      return {
        WAITING_STOCK: 'is-info',
        PROCESSING: 'is-warning',
        UNPACKED: 'is-primary',
        PACKED: 'is-info',
        DISPATCHED: 'is-success',
        IN_TRANSIT: 'is-info',
        DELIVERED: 'is-success',
        CANCELED: 'is-danger',
      }[status] || 'is-default';
    },
    getFormattedDate(date) {
      return Moment(date).format('DD/MM/YYYY HH:mm');
    },
    sort(sortField, sortOrder) {
      Object.assign(this.filters, {
        sortField,
        sortOrder,
      });

      this.getOrderList();
    },
    onPageChange(page) {
      Object.assign(this.filters, {
        page,
      });

      this.getOrderList();
    },
    changePerPage(limit) {
      Object.assign(this.filters, {
        limit,
      });

      this.getOrderList();
    },
    applyFilters(filters) {
      this.getOrderList({
        ...this.filters,
        ...filters || {},
      });
    },
    async getOrderList(filters) {
      await this.getOrders(filters || this.filters);
    },
    editOrder(orderId) {
      this.$router.push({
        name: 'orderDetail',
        params: {
          id: orderId,
        },
      });
    },
  },
  async beforeMount() {
    console.log('mounted: ');
    await this.getOrderList();
  },
});
</script>

<style lang="scss">
  .orders {
    &__title-box{
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    &__title{
      padding-bottom: 1rem;
    }
    &__line-through {
      text-decoration: line-through;
    }
  }
</style>
